import { lazy, Suspense, useState, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { WalletContextProvider } from "@/contexts/WalletContext";
import { supabase } from "@/integrations/supabase/client";
import { useAuthErrorHandler } from "@/hooks/useAuthErrorHandler";
import Footer from "@/components/Footer";

// Lazy load page components for code splitting
const Landing = lazy(() => import("./pages/Landing"));
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const SignalHistory = lazy(() => import("./pages/SignalHistory"));
const Plans = lazy(() => import("./pages/Plans"));
const Subscription = lazy(() => import("./pages/Subscription"));
const ReferralProgram = lazy(() => import("./pages/ReferralProgram"));
const ReferralRedirect = lazy(() => import("./pages/ReferralRedirect"));
const Admin = lazy(() => import("./pages/Admin"));
const MarketNews = lazy(() => import("./pages/MarketNews"));
const MarketShock = lazy(() => import("./pages/MarketShock"));
const Chart = lazy(() => import("./pages/Chart"));
const Help = lazy(() => import("./pages/Help"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Documentation = lazy(() => import("./pages/Documentation"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const AppContent = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const location = useLocation();
  
  // Global auth error handling
  useAuthErrorHandler();
  
  // Hide footer on full-screen pages
  const hideFooter = location.pathname === '/chart';

  useEffect(() => {
    // Check initial auth state
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsAuthenticated(!!session);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <div className="flex flex-col min-h-screen">
        <div className="flex-1">
          <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-pulse">Loading...</div></div>}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/dashboard" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/history" element={<SignalHistory />} />
              <Route path="/plans" element={<Plans />} />
              <Route path="/subscription" element={<Subscription />} />
              <Route path="/referral" element={<ReferralProgram />} />
              <Route path="/ref/:userId" element={<ReferralRedirect />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/news" element={<MarketNews />} />
              <Route path="/shock" element={<MarketShock />} />
              <Route path="/chart" element={<Chart />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/documentation" element={<Documentation />} />
              <Route path="/help" element={<Help />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </div>
        {!hideFooter && <Footer />}
      </div>
    </TooltipProvider>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <WalletContextProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </WalletContextProvider>
    </QueryClientProvider>
  );
};

export default App;
